import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation } from '@/components'
import { AppButton } from '@/components/AppButton'
import { ArcProgressBar } from '@/components/ArcProgressBar'
import { ModalActions } from '@/components/ModalActions'
import { DeleteGoalModal } from '@/components/DeleteGoalModal'
import { useSavingsStore } from '@/store/useSavingsStore'
import { useGetSavingGoal } from '@/domain/transfer'
import { GoalCard, SavingsLastActivitiesList } from '@/features/savings'
import useOffsets from '@/hooks/useOffsets'
import { useTheme } from '@/hooks/useTheme'

export interface SavingsDetailParams
  extends Record<string, string | undefined> {
  goalId: string
}

export const SavingsDetailScreen: React.FC = () => {
  const { goalId } = useParams<SavingsDetailParams>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const offsets = useOffsets()

  const [modalActionsIsVisible, setModalActionsIsVisible] = useState(false)
  const [deleteGoalModalIsVisible, setDeleteGoalModalIsVisible] =
    useState(false)

  const setGoalTransferIn = useSavingsStore((state) => state.setGoalTransferIn)
  const setGoalTransferOut = useSavingsStore(
    (state) => state.setGoalTransferOut,
  )
  const resetAll = useSavingsStore((state) => state.resetAll)

  // Get goal data via API
  const {
    data: savingGoal,
    isLoading: isLoadingSavingDetails,
    error,
    refetch: refetchSavingGoal,
  } = useGetSavingGoal({
    saving_goal_id: goalId || '',
  })

  // Refresh data when returning to page (focus)
  useEffect(() => {
    const handleFocus = () => {
      refetchSavingGoal()
    }

    window.addEventListener('focus', handleFocus)

    // Also refresh on component mount
    refetchSavingGoal()

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [refetchSavingGoal])

  const hasSavedAmount = Number(savingGoal?.goal_percentage) > 0

  // If goal not found, show error
  if (error) {
    return (
      <BaseView
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <BaseText
          style={{ color: theme.colors.label.primary, textAlign: 'center' }}
        >
          {t('screens:goal.notFound')}
        </BaseText>
        <AppButton
          type="primaryGradient"
          onPress={() => navigate('/dashboard')}
          style={{ marginTop: '20px' }}
          label={t('form:actions.goBack')}
        />
      </BaseView>
    )
  }

  const handleTransferIn = () => {
    if (savingGoal) {
      setGoalTransferIn({
        saving_goal_id: savingGoal.id,
        category: savingGoal.category,
      })
      navigate('/savings/transfer-in')
    }
  }

  const handleTransferOut = () => {
    if (savingGoal) {
      setGoalTransferOut({
        saving_goal_id: savingGoal.id,
      })
      navigate('/savings/transfer-out')
    }
  }

  const handleDeleteGoal = () => {
    setModalActionsIsVisible(false)
    setTimeout(() => {
      setDeleteGoalModalIsVisible(true)
    }, 150)
  }

  const handleAfterDelete = () => {
    resetAll()
    setTimeout(() => {
      navigate('/dashboard')
    }, 200)
  }

  return (
    <BaseView
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: `${offsets.bottom}px`,
      }}
    >
      {/* Header Navigation */}
      <StickyHeaderNavigation
        title={t('translation:savings')}
        onBackPress={() => navigate('/dashboard')}
        showBackground={true}
        onMoreOptionsPress={
          !isLoadingSavingDetails
            ? () => setModalActionsIsVisible(true)
            : undefined
        }
      />

      {/* Content */}
      <BaseView
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: `${offsets.top + 74}px`,
          paddingLeft: '13px',
          paddingRight: '13px',
        }}
        gap={40}
      >
        {/* Arc Progress Bar */}
        <BaseView>
          <ArcProgressBar
            value={savingGoal?.stakedMaskedStableCoinAsFiat || '$0'}
            displayCurrencyValue={savingGoal?.stakedFiatAmountFormatted || '$0'}
            total={savingGoal?.goalAmountFormatted || '$0'}
            totalEarned={savingGoal?.currentEarningsFormatted}
            remainingToGoal={savingGoal?.remainingToGoalFormatted}
            percentage={Number(savingGoal?.goal_percentage || 0)}
            isLoading={isLoadingSavingDetails}
          />
        </BaseView>

        {/* Transfer Buttons */}
        <BaseView row align="center" gap={16}>
          <AppButton
            type="primaryGradient"
            size="medium"
            isSharedSpace
            style={{ borderRadius: 26, height: 56 }}
            onPress={handleTransferIn}
            disabled={isLoadingSavingDetails}
            isLoading={isLoadingSavingDetails}
            label={t('form:actions.transferIn')}
          />
          <AppButton
            type="primaryGradient"
            size="medium"
            isSharedSpace
            style={{ borderRadius: 26, height: 56 }}
            onPress={handleTransferOut}
            disabled={isLoadingSavingDetails}
            isLoading={isLoadingSavingDetails}
            label={t('form:actions.transferOut')}
          />
        </BaseView>

        {/* Goal Card and Activities */}
        <BaseView gap={16}>
          <GoalCard
            savingGoal={savingGoal}
            skeletonActive={isLoadingSavingDetails}
          />

          <SavingsLastActivitiesList
            goalId={goalId || ''}
            skeletonActive={isLoadingSavingDetails}
            transfers={savingGoal?.transfers || []}
          />
        </BaseView>
      </BaseView>

      {/* Modal Actions */}
      <ModalActions
        isVisible={modalActionsIsVisible}
        onClose={() => setModalActionsIsVisible(false)}
        actions={[
          {
            label: t('translation:goal.deleteGoal'),
            action: handleDeleteGoal,
            isDestructive: true,
          },
        ]}
      />

      {/* Delete Goal Modal */}
      <DeleteGoalModal
        isVisible={deleteGoalModalIsVisible}
        onClose={() => setDeleteGoalModalIsVisible(false)}
        goalId={goalId || ''}
        hasSavedAmount={hasSavedAmount}
        onAfterDelete={handleAfterDelete}
      />
    </BaseView>
  )
}

export default SavingsDetailScreen
